// frontend/src/pages/WalletPage.jsx

import React, { useState, useEffect, useMemo } from "react";
import axiosAuth from "../axiosConfig";
import { useNavigate, NavLink, Routes, Route } from "react-router-dom";
import { motion } from "framer-motion";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

// theme-aware tabs (light preserved, dark added)
const TAB_CLASSES = ({ isActive }) =>
  `px-2 py-1 text-sm sm:px-4 sm:py-2 sm:text-base font-medium ${
    isActive
      ? "text-blue-800 dark:text-white border-b-2 border-blue-600 dark:border-blue-500"
      : "text-gray-600 dark:text-neutral-400 hover:text-blue-800 hover:dark:text-white"
  }`;

const COLORS = ["#1D4ED8", "#3B82F6", "#60A5FA", "#93C5FD", "#BFDBFE"];
const COINGECKO_IDS = { BTC: "bitcoin", ETH: "ethereum", USDT: "tether" };

// helpers
const toISODate = (d) => new Date(d).toISOString().slice(0, 10);
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

export default function WalletPage() {
  const [user, setUser] = useState(null);
  const [balances, setBalances] = useState(null);
  const [marketData, setMarketData] = useState({});
  const [error, setError] = useState("");
  const navigate = useNavigate();

  // load user & balances
  useEffect(() => {
    (async () => {
      try {
        const { data: who } = await axiosAuth.get("/whoami");
        setUser(who.user);
        const { data: bal } = await axiosAuth.get(
          `/balance/${encodeURIComponent(who.user.email)}`
        );
        setBalances(bal.balances ?? bal);
      } catch {
        setError("Unable to load wallet. Please log in.");
      }
    })();
  }, [navigate]);

  // fetch market prices
  useEffect(() => {
    fetch(
      `https://api.coingecko.com/api/v3/simple/price?ids=${Object.values(
        COINGECKO_IDS
      ).join(",")} &vs_currencies=inr`.replace(" &", "&")
    )
      .then((r) => r.json())
      .then((data) => setMarketData(data))
      .catch(console.error);
  }, []);

  // lookups & totals
  const priceLookup = useMemo(
    () =>
      Object.entries(COINGECKO_IDS).reduce((acc, [sym, id]) => {
        acc[sym] = marketData[id]?.inr || 0;
        return acc;
      }, {}),
    [marketData]
  );

  const chartData = useMemo(() => {
    if (!balances) return [];
    return Object.entries(balances)
      .filter(([, amt]) => Number(amt) > 0)
      .map(([name, amt]) => ({
        name,
        value:
          name === "INR" ? Number(amt) : Number(amt) * (priceLookup[name] || 0),
      }));
  }, [balances, priceLookup]);

  const totalValue = useMemo(() => {
    if (!balances) return 0;
    return Object.entries(balances).reduce((sum, [name, amt]) => {
      const n = Number(amt) || 0;
      return name === "INR" ? sum + n : sum + n * (priceLookup[name] || 0);
    }, 0);
  }, [balances, priceLookup]);

  // error / loading
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-blue-50 dark:bg-black">
        <div className="px-4">
          <p className="text-lg sm:text-2xl text-red-600 dark:text-red-500">{error}</p>
          <button
            onClick={() => navigate("/login")}
            className="mt-3 sm:mt-4 text-sm sm:text-lg text-blue-600 dark:text-blue-400 hover:underline"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }
  if (!user || balances === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-blue-50 dark:bg-black">
        <p className="text-lg sm:text-2xl text-blue-600 dark:text-blue-400">Loading wallet …</p>
      </div>
    );
  }

  return (
    <motion.div
      className="min-h-screen bg-blue-50 dark:bg-black pb-8 sm:pb-10"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header (light unchanged, dark text tweaked) */}
      <header className="bg-white dark:bg-black shadow-lg border-b-4 border-blue-600">
        <div className="max-w-7xl mx-auto w-full px-3 sm:px-6 py-3 sm:py-6">
          <div className="flex flex-row items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-xl sm:text-3xl font-bold text-blue-800 dark:text-white leading-tight">
                Wallet Dashboard
              </h1>
              <p className="text-blue-600 dark:text-blue-400 text-xs sm:text-base">
                Manage your INR &amp; Crypto
              </p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-[10px] sm:text-sm text-gray-600 dark:text-neutral-400">Total Value</p>
              <p className="text-lg sm:text-2xl font-extrabold text-blue-700 dark:text-blue-400 leading-snug whitespace-nowrap">
                ₹{" "}
                {totalValue.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                })}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Quick Actions (light kept, dark bg/text) */}
      <motion.div
        className="max-w-7xl mx-auto w-full px-3 sm:px-6 mt-4 sm:mt-6 bg-white dark:bg-black rounded-2xl shadow-lg p-4 sm:p-6 flex flex-col sm:flex-row items-center sm:justify-between gap-3 sm:gap-0 border-l-4 border-blue-500"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <p className="text-blue-700 dark:text-blue-400 font-medium text-sm sm:text-base md:text-lg">Quick Actions</p>
        <div className="flex flex-nowrap gap-2 sm:gap-4 md:ml-auto overflow-x-auto scrollbar-hide">
          <button
            onClick={() => navigate("/deposit")}
            className="bg-blue-600 text-white px-3 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-base rounded-lg sm:rounded-xl hover:bg-blue-700 transition shadow-sm whitespace-nowrap"
          >
            Deposit
          </button>
          <button
            onClick={() => navigate("/withdraw")}
            className="bg-blue-600 text-white px-3 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-base rounded-lg sm:rounded-xl hover:bg-blue-700 transition shadow-sm whitespace-nowrap"
          >
            Withdraw
          </button>
          <button
            onClick={() => navigate("/p2p")}
            className="bg-blue-600 text-white px-3 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-base rounded-lg sm:rounded-xl hover:bg-blue-700 transition shadow-sm whitespace-nowrap"
          >
            P2P Market
          </button>
          <button
            onClick={() => navigate("/quick-trade")}
            className="bg-blue-600 text-white px-3 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-base rounded-lg sm:rounded-xl hover:bg-blue-700 transition shadow-sm whitespace-nowrap"
          >
            Quick Trade
          </button>
        </div>
      </motion.div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto w-full px-3 sm:px-6 mt-4 sm:mt-6 border-b border-gray-200 dark:border-neutral-800">
        <nav className="-mb-px flex flex-wrap gap-x-3 gap-y-2 sm:gap-x-6">
          <NavLink to="/wallet" end className={TAB_CLASSES}>
            Overview
          </NavLink>
          <NavLink to="/wallet/inr" className={TAB_CLASSES}>
            INR Wallet
          </NavLink>
          <NavLink to="/wallet/crypto" className={TAB_CLASSES}>
            Crypto Wallet
          </NavLink>
          <NavLink to="/wallet/statement/inr" className={TAB_CLASSES}>
            INR Statement
          </NavLink>
          <NavLink to="/wallet/statement/crypto" className={TAB_CLASSES}>
            Crypto Statement
          </NavLink>
          <NavLink to="/wallet/fee-tax" className={TAB_CLASSES}>
            Fee &amp; Tax
          </NavLink>
        </nav>
      </div>

      {/* Content + Pie Chart */}
      <main className="max-w-7xl mx-auto w-full px-3 sm:px-6 mt-4 sm:mt-6 flex flex-col lg:flex-row gap-4 sm:gap-6">
        <div className="flex-1 space-y-4 sm:space-y-6">
          <Routes>
            <Route path="" element={<Overview balances={balances} />} />
            <Route path="inr" element={<INRWallet balance={balances.INR} />} />
            <Route path="crypto" element={<CryptoWallet balances={balances} />} />
            <Route path="statement/inr" element={<StatementView scope="inr" />} />
            <Route path="statement/crypto" element={<StatementView scope="crypto" />} />
            <Route path="fee-tax" element={<FeeTax />} />
            <Route path="*" element={<Overview balances={balances} />} />
          </Routes>
        </div>

        <div className="w-full lg:w-1/3">
          <div className="bg-white dark:bg-black rounded-2xl shadow-lg p-4 sm:p-6 border-t-4 border-blue-500">
            <h3 className="text-lg sm:text-xl font-semibold text-blue-800 dark:text-white mb-3 sm:mb-4">
              Portfolio Breakdown
            </h3>
            {chartData.length ? (
              <div className="h-48 sm:h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={70}
                      label={({ name, percent }) =>
                        `${name} ${(percent * 100).toFixed(0)}%`
                      }
                    >
                      {chartData.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(val) =>
                        Number(val || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })
                      }
                    />
                    <Legend verticalAlign="bottom" height={32} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-center text-gray-500 dark:text-neutral-400 text-sm">No balances to show</p>
            )}
          </div>
        </div>
      </main>
    </motion.div>
  );
}

// — sub-components —
function Overview({ balances }) {
  return (
    <div className="space-y-3 sm:space-y-6">
      <Card title="INR Balance">
        <p className="text-xl sm:text-2xl font-bold">
          ₹ {Number(balances.INR || 0).toLocaleString()}
        </p>
      </Card>
      <Card title="Crypto Balances">
        <ul className="divide-y divide-gray-200 dark:divide-neutral-800">
          {Object.entries(balances)
            .filter(([k]) => k !== "INR")
            .map(([symbol, amt]) => (
              <li
                key={symbol}
                className="py-2 sm:py-3 flex justify-between text-sm sm:text-lg"
              >
                <span>{symbol}</span>
                <span className="whitespace-nowrap">
                  {Number(amt).toLocaleString()}
                </span>
              </li>
            ))}
        </ul>
      </Card>
    </div>
  );
}

function INRWallet({ balance }) {
  return (
    <Card title="INR Wallet Details">
      <p className="text-sm sm:text-base">
        Available INR:{" "}
        <span className="font-semibold">
          ₹ {Number(balance || 0).toLocaleString()}
        </span>
      </p>
    </Card>
  );
}

function CryptoWallet({ balances }) {
  return (
    <Card title="Crypto Wallet Details">
      <ul className="divide-y divide-gray-200 dark:divide-neutral-800">
        {Object.entries(balances)
          .filter(([k]) => k !== "INR")
          .map(([symbol, amt]) => (
            <li
              key={symbol}
              className="py-2 sm:py-3 flex justify-between text-sm sm:text-lg"
            >
              <span>{symbol}</span>
              <span className="whitespace-nowrap">
                {Number(amt).toLocaleString()}
              </span>
            </li>
          ))}
      </ul>
    </Card>
  );
}

/** ===== Statement UI (INR / Crypto) ===== */
function StatementView({ scope }) {
  const title = scope === "inr" ? "INR Statement" : "Crypto Statement (USDT & trades)";

  // default: last 30 days
  const today = toISODate(new Date());
  const last30 = toISODate(addDays(new Date(), -30));

  const [from, setFrom] = useState(last30);
  const [to, setTo] = useState(today);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [error, setError] = useState("");

  const isINR = scope === "inr";
  const isCrypto = scope === "crypto";

  const totals = useMemo(() => {
    const sum = (k) => items.reduce((acc, it) => acc + (Number(it[k]) || 0), 0);
    if (isINR) {
      return {
        amount: sum("amount"),
        platformFee: sum("platformFee"),
        gst: sum("gst"),
        net: sum("net"),
      };
    }
    return {
      amount: sum("amount"),
      platformFee: sum("platformFee"),
      networkFee: sum("networkFee"),
      net: sum("net"),
    };
  }, [items, isINR]);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const { data } = await axiosAuth.get("/statements/user", {
        params: { scope, from, to },
      });
      setItems(data.items || data.transactions || data || []);
    } catch (e) {
      try {
        if (isCrypto) {
          const { data } = await axiosAuth.get("/withdraw/crypto");
          const rows = (data.withdrawals || []).map((w) => {
            const amount = Number(w.amount || 0);
            const pf = Number(w.platformFee || w.charge || 0);
            const nf = Number(w.networkFee || w.fee || 0);
            return {
              id: w.id || w._id,
              date: w.date || w.createdAt,
              side: "withdraw",
              asset: w.coin || "USDT",
              amount,
              platformFee: pf,
              networkFee: nf,
              status: w.status,
              net: amount + pf + nf,
            };
          });
          setItems(rows);
        } else {
          const { data } = await axiosAuth.get("/withdraw/fiat");
          const rows = (data.withdrawals || []).map((w) => ({
            id: w.id || w._id,
            date: w.date || w.createdAt,
            side: "withdraw",
            asset: "INR",
            amount: Number(w.amount || 0),
            platformFee: Number(w.platformFee ?? w.charge ?? 0),
            gst: Number(w.gst || 0),
            networkFee: 0,
            status: w.status,
            net: Number(
              w.net ??
                w.netAmount ??
                (Number(w.amount || 0) -
                  Number(w.platformFee ?? w.charge ?? 0) -
                  Number(w.gst || 0))
            ),
          }));
          setItems(rows);
        }
      } catch {
        setError("Could not load statement. Please try again or adjust the date range.");
      }
    } finally {
      setLoading(false);
    }
  }

  async function download(format = "csv") {
    setDownloading(true);
    try {
      const resp = await axiosAuth.get("/statements/user/download", {
        params: { scope, from, to, format },
        responseType: "blob",
      });
      const blob = new Blob([resp.data], {
        type: format === "csv" ? "text/csv" : "application/pdf",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `statement_${scope}_${from}_${to}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      try {
        const csv = toCSV(items);
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `statement_${scope}_${from}_${to}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch {
        alert("Download failed.");
      }
    } finally {
      setDownloading(false);
    }
  }

  async function sendEmail() {
    setEmailing(true);
    try {
      await axiosAuth.post("/statements/user/send-email", {
        scope,
        from,
        to,
        format: "pdf",
      });
      alert("Statement email queued. Check your inbox.");
    } catch {
      alert("Email send failed.");
    } finally {
      setEmailing(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  return (
    <Card title={title}>
      {/* Controls */}
      <div className="flex flex-col md:flex-row gap-2 sm:gap-3 md:items-end">
        <div>
          <label className="block text-[11px] sm:text-sm text-gray-600 dark:text-neutral-400">From</label>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="border rounded px-2 py-1.5 sm:px-3 sm:py-2 text-sm bg-white dark:bg-black border-gray-300 dark:border-neutral-800"
          />
        </div>
        <div>
          <label className="block text-[11px] sm:text-sm text-gray-600 dark:text-neutral-400">To</label>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="border rounded px-2 py-1.5 sm:px-3 sm:py-2 text-sm bg-white dark:bg-black border-gray-300 dark:border-neutral-800"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={load}
            disabled={loading}
            className="bg-blue-600 text-white px-3 py-1.5 sm:px-4 sm:py-2 text-sm rounded hover:bg-blue-700 disabled:opacity-60"
          >
            {loading ? "Loading…" : "Load"}
          </button>
          <button
            onClick={() => download("csv")}
            disabled={downloading || !items.length}
            className="bg-gray-800 dark:bg-neutral-900 text-white px-3 py-1.5 sm:px-4 sm:py-2 text-sm rounded hover:bg-black dark:hover:bg-neutral-800 disabled:opacity-60"
          >
            {downloading ? "Preparing…" : "Download CSV"}
          </button>
          <button
            onClick={sendEmail}
            disabled={emailing || !items.length}
            className="bg-emerald-600 text-white px-3 py-1.5 sm:px-4 sm:py-2 text-sm rounded hover:bg-emerald-700 disabled:opacity-60"
          >
            {emailing ? "Sending…" : "Email PDF"}
          </button>
        </div>
      </div>

      {/* Table */}
      {error ? (
        <p className="text-red-600 mt-3 sm:mt-4 text-sm sm:text-base">{error}</p>
      ) : (
        <div className="mt-3 sm:mt-4 overflow-auto">
          <table className="min-w-full table-auto text-[11px] sm:text-sm">
            <thead className="bg-blue-50 dark:bg-neutral-900">
              <tr>
                <Th>Dt</Th>
                <Th>Type</Th>
                <Th>Asset</Th>
                <Th className="text-right">
                  <span className="sm:hidden">Amt</span>
                  <span className="hidden sm:inline">Amount</span>
                </Th>
                <Th className="text-right">
                  <span className="sm:hidden">Plat.Fee</span>
                  <span className="hidden sm:inline">Platform Fee</span>
                </Th>
                {isCrypto && (
                  <Th className="text-right">
                    <span className="sm:hidden">Net.Fee</span>
                    <span className="hidden sm:inline">Network Fee</span>
                  </Th>
                )}
                {isINR && <Th className="text-right">GST</Th>}
                <Th className="text-right">Net</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id || it._id} className="align-top">
                  <Td className="whitespace-nowrap">{formatDate(it.date || it.createdAt)}</Td>
                  <Td className="whitespace-nowrap">{it.side || it.type || it.kind || "—"}</Td>
                  <Td className="whitespace-nowrap">{it.asset || it.coin || it.symbol || (isINR ? "INR" : "USDT")}</Td>
                  <Td className="text-right whitespace-nowrap">{fmt(it.amount)}</Td>
                  <Td className="text-right whitespace-nowrap">{fmt(it.platformFee || it.charge)}</Td>
                  {isCrypto && (
                    <Td className="text-right whitespace-nowrap">{fmt(it.networkFee || it.fee)}</Td>
                  )}
                  {isINR && <Td className="text-right whitespace-nowrap">{fmt(it.gst)}</Td>}
                  <Td className="text-right whitespace-nowrap">{fmt(it.net || it.netAmount)}</Td>
                  <Td className="whitespace-nowrap">{it.status || "—"}</Td>
                </tr>
              ))}
              {!items.length && !loading && (
                <tr>
                  <Td colSpan={isINR ? 8 : 8} className="text-center text-gray-500 dark:text-neutral-400 py-5">
                    No records for the selected range.
                  </Td>
                </tr>
              )}
            </tbody>
            <tfoot className="bg-blue-50 dark:bg-neutral-900">
              <tr>
                <Td colSpan={3} className="font-semibold">Totals</Td>
                <Td className="text-right whitespace-nowrap">{fmt(totals.amount)}</Td>
                <Td className="text-right whitespace-nowrap">{fmt(totals.platformFee)}</Td>
                {isCrypto && (
                  <Td className="text-right whitespace-nowrap">{fmt(totals.networkFee)}</Td>
                )}
                {isINR && <Td className="text-right whitespace-nowrap">{fmt(totals.gst)}</Td>}
                <Td className="text-right whitespace-nowrap">{fmt(totals.net)}</Td>
                <Td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </Card>
  );
}

/** ===== Fee & Tax (user-facing) ===== */
function FeeTax() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({
    platformFee: 0,
    gst: 0,
    tds: 0,
    networkFee: 0,
  });
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");
      try {
        const [spot, wCrypto] = await Promise.allSettled([
          axiosAuth.get("/trades/trades"),
          axiosAuth.get("/withdraw/crypto"),
        ]);

        const spotRows =
          spot.status === "fulfilled"
            ? (spot.value.data.trades || []).map((t) => ({
                source: "Spot Trade",
                date: t.createdAt,
                asset: t.symbol || "—",
                platformFee: Number(t.platformFee || 0),
                gst: Number(t.gst || 0),
                tds: Number(t.tds || 0),
                networkFee: 0,
              }))
            : [];

        const wRows =
          wCrypto.status === "fulfilled"
            ? (wCrypto.value.data.withdrawals || []).map((w) => ({
                source: "Crypto Withdrawal",
                date: w.date || w.createdAt,
                asset: w.coin || "USDT",
                platformFee: Number(w.platformFee || w.charge || 0),
                gst: 0,
                tds: 0,
                networkFee: Number(w.networkFee || w.fee || 0),
              }))
            : [];

        const all = [...spotRows, ...wRows].sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );

        const sum = (k) => all.reduce((acc, r) => acc + (Number(r[k]) || 0), 0);
        setTotals({
          platformFee: sum("platformFee"),
          gst: sum("gst"),
          tds: sum("tds"),
          networkFee: sum("networkFee"),
        });
        setRows(all);
      } catch {
        setError("Could not load fee/tax summary.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <Card title="Fee & Tax Summary">
      {error && <p className="text-red-600 dark:text-red-500 mb-3 text-sm sm:text-base">{error}</p>}
      {loading ? (
        <p className="text-sm sm:text-base">Loading…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-3 sm:mb-4">
            <Stat label="Platform Fees (Total)" value={fmt(totals.platformFee)} />
            <Stat label="GST (Total)" value={fmt(totals.gst)} />
            <Stat label="TDS Withheld (Total)" value={fmt(totals.tds)} />
            <Stat label="Network Fees (Total)" value={fmt(totals.networkFee)} />
          </div>

          <div className="overflow-auto">
            <table className="min-w-full table-auto text-[11px] sm:text-sm">
              <thead className="bg-blue-50 dark:bg-neutral-900">
                <tr>
                  <Th>Dt</Th>
                  <Th>Source</Th>
                  <Th>Asset</Th>
                  <Th className="text-right">
                    <span className="sm:hidden">Plat.Fee</span>
                    <span className="hidden sm:inline">Platform Fee</span>
                  </Th>
                  <Th className="text-right">GST</Th>
                  <Th className="text-right">TDS</Th>
                  <Th className="text-right">
                    <span className="sm:hidden">Net.Fee</span>
                    <span className="hidden sm:inline">Network Fee</span>
                  </Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="align-top">
                    <Td className="whitespace-nowrap">{formatDate(r.date)}</Td>
                    <Td className="whitespace-nowrap">{r.source}</Td>
                    <Td className="whitespace-nowrap">{r.asset}</Td>
                    <Td className="text-right whitespace-nowrap">{fmt(r.platformFee)}</Td>
                    <Td className="text-right whitespace-nowrap">{fmt(r.gst)}</Td>
                    <Td className="text-right whitespace-nowrap">{fmt(r.tds)}</Td>
                    <Td className="text-right whitespace-nowrap">{fmt(r.networkFee)}</Td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr>
                    <Td colSpan={7} className="text-center text-gray-500 dark:text-neutral-400 py-5">
                      No fee/tax records found.
                    </Td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
}

// — small UI bits (compact on phone) —
function Card({ title, children }) {
  return (
    <div className="bg-white dark:bg-black rounded-2xl shadow-lg p-4 sm:p-6 border-t-4 border-blue-500">
      <h3 className="text-lg sm:text-xl font-semibold text-blue-800 dark:text-white mb-2">{title}</h3>
      {children}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-blue-50 dark:bg-neutral-900 rounded-xl p-3 sm:p-4 border border-blue-100 dark:border-neutral-800">
      <p className="text-xs sm:text-sm text-blue-700 dark:text-blue-400">{label}</p>
      <p className="text-lg sm:text-xl font-semibold text-blue-900 dark:text-white">{value}</p>
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`text-left px-2 py-1.5 sm:px-3 sm:py-2 font-semibold text-blue-900 dark:text-white text-[11px] sm:text-sm ${className}`}
    >
      {children}
    </th>
  );
}
function Td({ children, className = "", colSpan }) {
  return (
    <td
      className={`px-2 py-1.5 sm:px-3 sm:py-2 border-b border-gray-200 dark:border-neutral-800 text-[11px] sm:text-sm ${className}`}
      colSpan={colSpan}
    >
      {children}
    </td>
  );
}

function formatDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "—";
  return (
    dt.toLocaleString(undefined, {
      year: "2-digit",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }) || "—"
  );
}

function fmt(n) {
  const x = Number(n || 0);
  if (!Number.isFinite(x)) return "—";
  return x.toLocaleString(undefined, { maximumFractionDigits: 8 });
}

function toCSV(items) {
  if (!items || !items.length) return "No data";
  const cols = [
    "date",
    "side",
    "type",
    "asset",
    "amount",
    "platformFee",
    "networkFee",
    "gst",
    "tds",
    "net",
    "status",
    "txHash",
    "address",
  ];
  const header = cols.join(",");
  const lines = items.map((it) =>
    cols
      .map((k) => {
        const v =
          it[k] ??
          it[k === "asset" ? "coin" : k] ??
          it[k === "platformFee" ? "charge" : k] ??
          it[k === "networkFee" ? "fee" : k] ??
          "";
        const s = String(v ?? "");
        return /,|\n|"/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      })
      .join(",")
  );
  return [header, ...lines].join("\n");
}
